import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Zap, FileText, Flame, Sparkles, ArrowUpRight, Star } from "lucide-react";
import api, { timeAgo } from "@/lib/api";
import { Article } from "@/lib/types";
import { useArticlePath } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { useWatchlist } from "@/components/WatchButton";
import { track } from "./navConfig";
import { SectorArt } from "./SectorArt";
import { NavMenuAd } from "@/components/AdSlot";
import {
  Micro, NavRow, PanelCTA, StoryRow, SkRow, GhostWord, LiveDot, CoinIcon,
  Pct, Spark, fmtPrice, fmtBig, MiniRing, TagChip, REGION, delay, FOCUS,
} from "./primitives";

const LiveCoin = ({ c }: { c: any }) => {
  const { data } = useQuery({
    queryKey: ["nav-spark24", c.symbol],
    queryFn: async () => (await api.get(`/markets/${c.symbol}?range=24h`)).data,
    staleTime: 5 * 60 * 1000,
  });
  const chart: number[] = (data?.chart || []).map((p: any) => p.price);
  return (
    <div className="flex items-center gap-2.5">
      <CoinIcon symbol={c.symbol} size={24} />
      <span className="font-mono text-xs font-bold text-gray-300 w-9">{c.symbol}</span>
      <Spark points={chart} up={c.change_24h >= 0} className="w-12 h-5" />
      <span className="font-mono text-sm text-white ml-auto">{fmtPrice(c.price)}</span>
      <Pct v={c.change_24h} className="w-14 text-right" />
    </div>
  );
};

const FeaturedStory = ({ a }: { a: Article }) => {
  const toArticle = useArticlePath();
  const cat = a.categories?.[0];
  return (
    <Link to={toArticle(a.slug)} data-testid="latest-trending-featured"
      onClick={() => track("nav_click", { story: a.slug, featured: true })}
      className={`group/feat relative flex gap-4 rounded-xl border border-white/[0.09] bg-white/[0.03] p-3 overflow-hidden
        hover:border-[#00E5FF]/30 hover:bg-white/[0.05] transition-colors duration-200 ${FOCUS}`}>
      <span className="relative block h-[88px] w-[148px] shrink-0 rounded-lg overflow-hidden border border-white/10 bg-[#0d0e14]">
        {a.hero_image_url ? (
          <img src={a.hero_image_url} alt="" loading="lazy"
            className="h-full w-full object-cover motion-safe:transition-transform motion-safe:duration-300 group-hover/feat:scale-[1.03]" />
        ) : (
          <SectorArt kind={cat || "crypto"} className="absolute inset-0 h-full w-full opacity-70" />
        )}
        <span aria-hidden className="absolute inset-0 bg-gradient-to-t from-black/45 via-transparent to-transparent" />
      </span>
      <span className="min-w-0 flex-1 py-0.5">
        <span className="flex items-center gap-2">
          <span className="font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-[#00E5FF]">#1 Trending</span>
          {cat && <TagChip>{cat === "government-policy" ? "policy" : cat}</TagChip>}
        </span>
        <span className="mt-1.5 block font-news text-[15px] font-bold text-white leading-snug line-clamp-2">
          {a.title}
        </span>
        <span className="mt-1.5 block font-mono text-[9px] uppercase tracking-wider text-gray-500">
          {timeAgo(a.published_at)}
        </span>
      </span>
      <ArrowUpRight aria-hidden className="h-4 w-4 shrink-0 mt-1 text-[#00E5FF] opacity-0 -translate-x-1
        group-hover/feat:opacity-100 group-hover/feat:translate-x-0 motion-safe:transition-all motion-safe:duration-200" />
    </Link>
  );
};

export const LatestMenu = () => {
  const { data: trending, isLoading } = useQuery({
    queryKey: ["nav-trending"],
    queryFn: async () => (await api.get("/trending/most-read")).data,
    staleTime: 2 * 60 * 1000,
  });
  const { data: markets } = useQuery({
    queryKey: ["markets"],
    queryFn: async () => (await api.get("/markets")).data,
    staleTime: 90 * 1000,
  });
  const { data: global } = useQuery({
    queryKey: ["markets-global"],
    queryFn: async () => (await api.get("/markets/global")).data,
    staleTime: 10 * 60 * 1000,
  });
  const { data: li } = useQuery({
    queryKey: ["layer-index"],
    queryFn: async () => (await api.get("/layer-index")).data,
    staleTime: 5 * 60 * 1000,
  });

  const { user } = useAuth();
  const { data: wl } = useWatchlist();

  const stories: Article[] = (trending?.items || []).slice(0, 3);
  const [featured, ...rest] = stories;
  const allCoins = markets?.coins || [];
  const liveSyms = ["BTC", "ETH", "SOL", "XRP"];
  const coins = allCoins.filter((c: any) => liveSyms.includes(c.symbol));
  const watchSyms: string[] = user ? (wl?.watchlist || []).map((w: any) => w.symbol) : [];
  const watchCoins = allCoins
    .filter((c: any) => watchSyms.includes(c.symbol) && !liveSyms.includes(c.symbol))
    .slice(0, 3);

  return (
    <div data-testid="layernav-latest" className="relative grid grid-cols-2 xl:grid-cols-[225px_1fr_296px] gap-x-8 gap-y-6 p-7">
      <GhostWord word="Latest" />

      <div className={REGION} style={delay(0)}>
        <Micro className="mb-4">Newsroom</Micro>
        <div className="space-y-1">
          <NavRow to="/latest" icon={Zap} label="Breaking" desc="Real-time developments" testid="latest-nav-breaking" />
          <NavRow to="/research" icon={FileText} label="Deep Dives" desc="Analysis & long-form reporting" testid="latest-nav-deep-dives" />
          <NavRow to="/latest" icon={Flame} label="Most Read" desc="Top reader attention" testid="latest-nav-most-read" />
          <NavRow to="/" icon={Sparkles} label="Editor's Picks" desc="Selected by NewsLayer" testid="latest-nav-editors-picks" />
        </div>
        <div className="border-t border-white/[0.07] mt-4 pt-4">
          <PanelCTA to="/latest" testid="latest-view-all">All Latest</PanelCTA>
        </div>
      </div>

      <div className={`min-w-0 ${REGION}`} style={delay(1)}>
        <Micro cyan className="mb-4">Trending Now<LiveDot /></Micro>
        {isLoading && <div className="space-y-1.5"><SkRow /><SkRow /><SkRow /></div>}
        {!isLoading && featured && (
          <>
            <FeaturedStory a={featured} />
            <div className="mt-2 space-y-0.5">
              {rest.map((a, i) => (
                <StoryRow key={a.slug} a={a} rank={i + 2} testid={`latest-trending-${i + 1}`} />
              ))}
            </div>
          </>
        )}
        {!isLoading && stories.length === 0 && (
          <p className="text-sm text-gray-500 py-4">Fresh stories are publishing — check the latest feed.</p>
        )}
        <div className="mt-3 pl-1">
          <PanelCTA to="/latest" testid="latest-view-trending">View all trending</PanelCTA>
        </div>
      </div>

      <div className={`col-span-2 xl:col-span-1 border-t xl:border-t-0 xl:border-l border-white/[0.07] pt-5 xl:pt-0 xl:pl-7 ${REGION}`} style={delay(2)}>
        <Micro cyan className="mb-4">Live Layer<LiveDot /></Micro>
        <div className="space-y-2.5" data-testid="latest-live-layer">
          {coins.map((c: any) => <LiveCoin key={c.symbol} c={c} />)}
          {coins.length === 0 && (
            <p className="font-mono text-[11px] text-gray-600">Market data temporarily unavailable</p>
          )}
          {global?.available && (
            <div className="flex items-center gap-2.5 border-t border-white/[0.07] pt-2.5">
              <span className="font-mono text-[9px] uppercase tracking-wider text-gray-500">Total MCap</span>
              <span className="font-mono text-sm text-white ml-auto">{fmtBig(global.total_market_cap_usd)}</span>
              <Pct v={global.market_cap_change_24h_pct} className="w-14 text-right" />
            </div>
          )}
          {watchCoins.length > 0 && (
            <div className="space-y-2.5 border-t border-white/[0.07] pt-2.5" data-testid="latest-watchlist">
              <p className="flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-wider text-gray-500">
                <Star aria-hidden className="h-3 w-3 fill-[#F59E0B] text-[#F59E0B]" /> My Watchlist
              </p>
              {watchCoins.map((c: any) => <LiveCoin key={c.symbol} c={c} />)}
            </div>
          )}
        </div>
        {li && (
          <div className="mt-4 rounded-xl border border-white/10 bg-white/[0.02] p-3.5" data-testid="latest-layer-index">
            <Micro className="mb-3 !tracking-[0.25em]">The Layer Index</Micro>
            <MiniRing value={li.value ?? 50} label={li.label || "Neutral"} />
          </div>
        )}
      </div>
      <NavMenuAd />
    </div>
  );
};
